import React, { useState } from 'react';
import { useLocation, useNavigate, Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import { supabase } from '../../lib/supabaseClient';
import { mapSupabaseErrorMessage } from './authErrors';
import AnimatedBackground from '../../components/AnimatedBackground';
import Logo from '../../components/Logo';
import SupportChat from '../../components/SupportChat';

const VerifyResetCodePage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation() as any;
  const [code, setCode] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const email = location?.state?.email as string | undefined;

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email) {
      toast.error('Missing email context');
      navigate('/forgot-password');
      return;
    }
    if (!/^\d{6}$/.test(code)) {
      toast.error('Enter the 6-digit code');
      return;
    }
    setIsLoading(true);
    const { data, error } = await supabase.auth.verifyOtp({
      email,
      token: code,
      type: 'recovery',
    } as any);
    if (error) {
      toast.error(mapSupabaseErrorMessage(error.message));
      setIsLoading(false);
      return;
    }
    toast.success('Code verified');
    navigate('/reset-password/new', { state: { email } });
    setIsLoading(false);
  };

  return (
    <div className="relative min-h-screen w-full flex items-start justify-center bg-[#0d1830] pt-20">
      <AnimatedBackground />
      <style>{`
        /* Override autofill styles for all states */
        input:-webkit-autofill,
        input:-webkit-autofill:hover,
        input:-webkit-autofill:focus,
        input:-webkit-autofill:active {
          -webkit-background-clip: text !important;
          -webkit-text-fill-color: white !important;
          transition: background-color 5000s ease-in-out 0s !important;
          box-shadow: inset 0 0 0 1000px rgba(255, 255, 255, 0.08) !important;
          caret-color: white !important;
        }
        
        /* Ensure the fields maintain styling when typing after autofill */
        .verify-code-input {
          background: rgba(255, 255, 255, 0.08) !important;
          backdrop-filter: blur(5px) !important;
          -webkit-backdrop-filter: blur(5px) !important;
          color: white !important;
          -webkit-text-fill-color: white !important;
        }
      `}</style>
      <div className="relative z-10 w-full max-w-2xl flex flex-col items-center">
        <div className="mb-4 pb-8 w-full">
          <Logo />
        </div>
        <div
          className="relative p-8 rounded-2xl w-full max-w-md"
          style={{
            background: 'rgba(0, 0, 0, 0.01)',
            backdropFilter: 'blur(3px) saturate(120%)',
            WebkitBackdropFilter: 'blur(3px) saturate(120%)',
            border: '0.5px solid rgba(255, 255, 255, 0.04)',
            boxShadow: '0 15px 32px 0 rgba(255, 255, 255, 0.2)'
          }}
        >
          <div className="text-center mb-8">
            <h2 className="text-2xl font-light text-white/90 tracking-wide">Verify Code</h2>
          </div>
          <form onSubmit={onSubmit} className="space-y-4">
            <input
              type="text"
              placeholder="6-digit code"
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/[^0-9]/g, '').slice(0, 6))}
              className="verify-code-input w-full px-3 py-2.5 rounded-lg text-white placeholder-white/90 border border-white/30 transition-all duration-300 focus:border-[#17c480] focus:ring-2 focus:ring-[#17c480] focus:outline-none text-center tracking-widest text-2xl"
            />
            <button
              type="submit"
              disabled={isLoading}
              className="w-full py-2.5 rounded-lg font-semibold tracking-wide transition-all duration-300 hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed"
              style={{
                background: 'rgba(23, 196, 128, 0.9)',
                backdropFilter: 'blur(10px)',
                WebkitBackdropFilter: 'blur(10px)',
                color: '#0d1830'
              }}
            >
              {isLoading ? 'Verifying…' : 'Verify'}
            </button>
          </form>
          <div className="mt-3 text-white text-base text-center">
            <span>
              Remember password?{' '}
              <Link to="/login" className="inline-block py-1 underline text-white hover:text-[#17c480] transition-colors focus:outline-none focus:ring-2 focus:ring-[#17c480] focus:ring-offset-2 focus:ring-offset-[#0d1830] rounded">
                Login
              </Link>
            </span>
          </div>
          <div className="mt-4 text-white text-xs text-center">
            <p>If you have an Engramiq account you will receive an email with password reset instructions. If you do not receive an email try signing up or contact <a href="mailto:support@engramiq.com" className="underline hover:text-[#17c480] transition-colors">support@engramiq.com</a> for assistance</p>
          </div>
        </div>
      </div>
      {/* <SupportChat /> */}
    </div>
  );
};

export default VerifyResetCodePage;

